import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Loader2, Paperclip, X } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { ACCEPT_ATTR, fileProblem, formatBytes } from "../lib/portalFiles";
import { cellOf, DECISIONS, dueLine } from "../lib/portalKinds";
import { uploadErrorText, uploadFile } from "../lib/portalUpload";
import { Chip, fieldCls, primaryBtn, secondaryBtn, Shell } from "../components/portal/PortalShell";
import SubmissionHistory from "../components/portal/SubmissionHistory";
import ParticipantAssessment from "../components/portal/ParticipantAssessment";

/**
 * `/portal/grupa/:groupId/cursant/:enrollmentId` — one participant, as their
 * trainer sees them: every assignment, every version handed in, the feedback
 * on each, and the form to write the next. Never their draft or their dossier.
 */
export default function PortalParticipant() {
  const { groupId, enrollmentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/portal/groups/${groupId}/participants/${enrollmentId}`)).data);
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am găsit cursantul."));
    }
  }, [groupId, enrollmentId]);

  useEffect(() => { load(); }, [load]);

  // Arriving from "De făcut" lands on the assignment in question.
  useEffect(() => {
    if (!data || !window.location.hash) return;
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [data]);

  return (
    <Shell wide>
      <Link to={`/portal/grupa/${groupId}`} className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi la grupă
      </Link>
      {error && <p className="mt-8 text-sm text-[#c0483f]" role="alert">{error}</p>}
      {!data && !error && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
      {data && (
        <>
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">{data.group.name}</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#373A45]">{data.participant.name}</h1>
          {data.participant.read_only_until && (
            <p className="mt-2 text-sm text-[#6B7280]">S-a retras de la curs. Își mai vede predările câteva zile, dar nu mai poate preda.</p>
          )}
          <ParticipantAssessment groupId={groupId} enrollmentId={enrollmentId} />
          {data.items.length === 0 && <p className="mt-8 text-sm text-[#6B7280]">Nicio temă publicată încă.</p>}
          <div className="mt-8 grid gap-10">
            {data.items.map((item) => <Assignment key={item.activity.id} item={item} reload={load} />)}
          </div>
        </>
      )}
    </Shell>
  );
}

function Assignment({ item, reload }) {
  const { activity, cell, submission: sub } = item;
  const look = cellOf(cell);
  return (
    <section id={`tema-${activity.id}`} className="scroll-mt-6" data-testid="portal-sheet-assignment">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-bold text-[#373A45]">{activity.title}</h2>
        <Chip tone={cell.late_feedback ? "late" : look.tone}>{look.label}</Chip>
      </div>
      <p className="mt-1 text-sm text-[#6B7280]">{dueLine(activity.due_at)}</p>
      {sub.versions.length === 0 ? (
        <p className="mt-3 text-sm text-[#6B7280]">Nu a predat nimic încă.</p>
      ) : (
        <>
          <SubmissionHistory versions={sub.versions} feedback={sub.feedback} title="Ce a predat"
            canEditFeedback onChanged={reload} />
          {sub.status !== "accepted" && <FeedbackSlot sub={sub} reload={reload} />}
        </>
      )}
    </section>
  );
}

/** Open while the latest version waits for an answer; one click away once it has one. */
function FeedbackSlot({ sub, reload }) {
  const [open, setOpen] = useState(false);
  if (sub.waiting_feedback || open) {
    return <FeedbackForm submissionId={sub.id} onSent={async () => { setOpen(false); await reload(); }} />;
  }
  return (
    <button type="button" onClick={() => setOpen(true)} className="mt-3 text-sm font-semibold text-[#965EA7] hover:underline">
      Mai adaugă un comentariu
    </button>
  );
}

function FeedbackForm({ submissionId, onSent }) {
  const [text, setText] = useState("");
  const [decision, setDecision] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const attach = async (list) => {
    for (const file of Array.from(list || [])) {
      const problem = fileProblem(file);
      if (problem) { setError(problem); continue; } // eslint-disable-line no-continue
      setUploading({ name: file.name, pct: 0 });
      try {
        const row = await uploadFile(`/portal/submissions/${submissionId}/feedback/uploads`, file, { // eslint-disable-line no-await-in-loop
          onProgress: (pct) => setUploading({ name: file.name, pct }),
        });
        setFiles((f) => [...f, row]);
      } catch (err) {
        setError(uploadErrorText(err, apiErrorMessage));
      }
      setUploading(null);
    }
  };

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/portal/submissions/${submissionId}/feedback`, { text, decision, files: files.map((f) => f.id) });
      setText("");
      setFiles([]);
      setDecision("");
      await onSent();
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut trimite feedbackul."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 grid gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="portal-feedback-form">
      <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
        Feedback pentru ultima versiune
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={8000} className={fieldCls}
          placeholder="Ce merge, ce mai e de lucrat." />
      </label>
      <fieldset className="grid gap-2">
        <legend className="text-sm font-semibold text-[#373A45]">Ce urmează</legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {Object.entries(DECISIONS).map(([key, d]) => (
            <label key={key}
              className={`cursor-pointer rounded-xl border p-3 text-sm transition-colors ${
                decision === key ? "border-[#965EA7] bg-[#965EA7]/[0.05]" : "border-[#E5E7EB] hover:border-[#965EA7]/40"}`}>
              <input type="radio" name={`decision-${submissionId}`} value={key} checked={decision === key}
                onChange={() => setDecision(key)} className="sr-only" />
              <span className="block font-semibold text-[#373A45]">{d.label}</span>
              <span className="mt-0.5 block text-xs text-[#6B7280]">{d.hint}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        {files.map((f) => (
          <p key={f.id} className="flex items-center gap-2 text-sm text-[#373A45]">
            <FileText size={14} /> {f.name} <span className="text-xs text-[#6B7280]">{formatBytes(f.size)}</span>
            <button type="button" aria-label={`Scoate ${f.name}`} onClick={() => setFiles(files.filter((x) => x.id !== f.id))}
              className="rounded p-1 text-[#6B7280] hover:text-[#c0483f]"><X size={13} /></button>
          </p>
        ))}
        {uploading && <p className="text-xs text-[#6B7280]">{uploading.name}: {uploading.pct}%</p>}
        <button type="button" onClick={() => inputRef.current?.click()} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#965EA7] hover:underline">
          <Paperclip size={14} /> Atașează un document corectat
        </button>
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
          onChange={(e) => { attach(e.target.files); e.target.value = ""; }} />
      </div>
      {error && <p className="text-sm text-[#c0483f]" role="alert">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={send} disabled={busy || Boolean(uploading) || !decision} className={primaryBtn} data-testid="portal-feedback-send">
          {busy && <Loader2 size={15} className="animate-spin" />} Trimite feedbackul
        </button>
        {text && <button type="button" onClick={() => setText("")} className={secondaryBtn}>Golește</button>}
      </div>
    </div>
  );
}
